import React from "react";
import { Button, Text } from "@xometry/xometry_loft";

import {
  DisabledFormHeader,
  DisabledFormItem,
  CloseButtonWrapper,
  PartOrderTotal,
} from "./DisabledForm.style";
import {
  MarketplaceFormElement,
  MarketplaceFormElementType,
} from "../../../schema/generated/schema";
import { FormDataFile } from "../../../utils/fileUtils";
import LineItemThumbnail from "../../proposals/ProposalForm/LineItems/LineItemRow/LineItemThumbnail";
import { DisabledPartMarking } from "./DisabledPartMarking";
import { formatAddressToLabel } from "../components/LoftUserAddress";

const { Address } = MarketplaceFormElementType;

interface DisabledFormTypes {
  elements: readonly MarketplaceFormElement[];
  schema: any;
  formData: { [key: string]: string | number | string[] | FormDataFile };
  elementArrangement: readonly string[];
  onClose: (() => void) | undefined;
}

export const DisabledForm = ({
  elements,
  schema,
  formData,
  elementArrangement,
  onClose,
}: DisabledFormTypes) => {
  const { title, description, properties } = schema;

  const needsDisplayValue = ["materialText", "processText", "finishing"];
  const needsEnumValue = formType => {
    return needsDisplayValue.includes(formType);
  };

  const renderFormValue = (elementTitle, elementValue) => {
    if (elementTitle === "file") {
      const file = formData[elementTitle] as FormDataFile;
      return (
        <>
          <LineItemThumbnail file={file} />
          <div>{file.originalFilename}</div>
        </>
      );
    }
    if (elementTitle === "partMarking") {
      return <DisabledPartMarking items={formData[elementTitle] as string[]} />;
    }
    if (elementTitle === "price") {
      const price = formData[elementTitle] as number;
      return `$${price.toFixed(2)}`;
    }
    if (needsEnumValue(elementTitle)) {
      return elements
        ?.find(element => element?.key === elementTitle)
        ?.optionValues?.find(value => value.value === elementValue).label;
    }
    const type = elements?.find(element => element?.key === elementTitle)?.type;
    if (type === Address) {
      return formatAddressToLabel(formData[elementTitle] as any);
    }
    return formData[elementTitle];
  };

  return (
    <>
      <DisabledFormHeader>
        <Text kind="panel-title">{title}</Text>
        <Text kind="body">{description}</Text>
      </DisabledFormHeader>
      {elementArrangement.map(element => {
        if (
          !formData[element] ||
          (element === "file" && !(formData[element] as FormDataFile).id)
        ) {
          return null;
        }
        return (
          <DisabledFormItem key={element}>
            <Text kind="body-sm-title">{properties[element].title}</Text>
            <Text kind="body-sm">
              {renderFormValue(element, formData[element])}
            </Text>
          </DisabledFormItem>
        );
      })}
      {formData.price && (
        <PartOrderTotal>
          <Text kind="body-title">Part Order Total</Text>
          <Text kind="body">
            $
            {(
              (formData.price as number) * (formData.quantity as number)
            ).toFixed(2)}
          </Text>
        </PartOrderTotal>
      )}
      <CloseButtonWrapper>
        <Button type="reset" kind="text" scale="large" onClick={onClose}>
          Close
        </Button>
      </CloseButtonWrapper>
    </>
  );
};
